import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserPlus } from "lucide-react";
import Tooltip from "components/crm/ui/Tooltip";
import useCloseOnEscape from "components/crm/ui/useCloseOnEscape";
import useText from "hooks/useText";

// Color of a person (same hash as app.admin's generateColorByName).
export function employeeColor(first, last) {
  const name = `${first || ""}${last || ""}`;
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${hash % 360}, 70%, 60%)`;
}

// A person's dot: the initials of their first and last name on a colored background.
export function PersonDot({ firstname, lastname, size = 24 }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full text-[10px] font-medium text-white"
      style={{
        width: size,
        height: size,
        backgroundColor: employeeColor(firstname, lastname),
      }}
    >
      {`${firstname?.[0] ?? ""}${lastname?.[0] ?? ""}`.toUpperCase()}
    </span>
  );
}

const fullName = (person) => `${person.firstname ?? ""} ${person.lastname ?? ""}`.trim();

// Pick the assigned person by id: their dot, name on hover. A click opens the team list (self
// first), in <body> so no scrolling list clips it, upwards when there is no room below.
// A value missing from `people` (someone who left the team) shows as unassigned.
// `readOnly`: the dot and the name on hover only.
const AssignMenu = ({
  value,
  people = [],
  meId,
  onChange,
  size = 24,
  empty,
  readOnly = false,
}) => {
  const { text } = useText("crmUi");
  const [box, setBox] = useState(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const current = people.find((person) => person.id === value) ?? null;
  const me = people.find((person) => person.id === meId);
  const ordered = me ? [me, ...people.filter((person) => person !== me)] : people;
  const assignLabel = empty || text("assign");
  const label = (person) =>
    person.id === meId
      ? text("meSuffix", { name: fullName(person) })
      : fullName(person);
  useEffect(() => {
    if (!box) return undefined;
    const onDown = (e) => {
      if (
        !trigger.current?.contains(e.target) &&
        !menu.current?.contains(e.target)
      )
        setBox(null);
    };
    const hide = () => setBox(null);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", hide, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", hide, true);
    };
  }, [box]);
  useCloseOnEscape(Boolean(box), () => setBox(null));
  const toggle = (e) => {
    e.stopPropagation();
    if (box) return setBox(null);
    const r = trigger.current.getBoundingClientRect();
    // Near the right edge, the list opens to the left, aligned on the dot.
    const x =
      r.left + 208 > window.innerWidth - 8
        ? { right: window.innerWidth - r.right }
        : { left: r.left };
    return setBox(
      window.innerHeight - r.bottom > 270
        ? { top: r.bottom + 4, ...x }
        : { bottom: window.innerHeight - r.top + 4, ...x }
    );
  };
  const choose = (e, id) => {
    e.stopPropagation();
    setBox(null);
    if (id !== value) onChange(id);
  };
  const dot = current ? (
    <PersonDot
      firstname={current.firstname}
      lastname={current.lastname}
      size={size}
    />
  ) : (
    <span
      className="flex shrink-0 items-center justify-center rounded-full border border-dashed border-faint text-faint"
      style={{ width: size, height: size }}
    >
      <UserPlus size={size / 2} />
    </span>
  );
  const hoverLabel = current ? label(current) : assignLabel;
  if (readOnly)
    return (
      <Tooltip label={hoverLabel}>
        <span role="img" aria-label={hoverLabel} className="flex p-0.5">
          {dot}
        </span>
      </Tooltip>
    );
  return (
    <>
      <Tooltip label={box ? null : hoverLabel}>
        <button
          ref={trigger}
          type="button"
          aria-label={assignLabel}
          aria-haspopup="menu"
          aria-expanded={Boolean(box)}
          onClick={toggle}
          className="flex rounded-full p-0.5 transition-shadow hover:ring-2 hover:ring-lineStrong"
        >
          {dot}
        </button>
      </Tooltip>
      {/* Portalled to <body>, out of <CrmScope>: the CRM font is set again on the menu. */}
      {box &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            style={box}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[70] max-h-64 w-52 overflow-y-auto rounded-lg border border-gray-100 bg-white py-1 font-crm shadow-lg"
          >
            {ordered.map((person) => (
              <button
                key={person.id}
                type="button"
                role="menuitem"
                onClick={(e) => choose(e, person.id)}
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 ${
                  person.id === value ? "bg-gray-50" : ""
                }`}
              >
                <PersonDot
                  firstname={person.firstname}
                  lastname={person.lastname}
                />
                <span className="truncate">{label(person)}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default AssignMenu;
